import PortalLayout from '../../../../layouts/PortalLayout';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Message, SectionBox, Tabs, FormStepper, theme, useAuth } from '@omnigenbiodata/ui';
import ReactMarkdown from 'react-markdown';
import { MdOutlineNoFood, MdOutlineToday, MdOutlinePerson, MdOutlinePeople } from 'react-icons/md';
import { RiFileDamageLine } from 'react-icons/ri';
import { STUDY_EMAIL } from '../../../../core/constants/content.constants';
import InstructionRow from './components/InstructionRow';
import TipRow from './components/TipRow';
import { useAppSelector } from '../../../../store';
import { responseSelector } from '../../../../store/participant';
import { OrderStatus } from '../../../../core/api/portal.types';
import useMediaQuery from '@mui/material/useMediaQuery';
import QRCode from 'react-qr-code';
import { Flags } from 'react-feature-flags';
import { FEATURES } from '../../../../core/constants/features.constants';

function SamplesScene() {
  const { t } = useTranslation('sampleCollection');
  const participant = useAppSelector(responseSelector);
  const { user } = useAuth();

  const getStatusStep = (orderStatus: string) => {
    switch (orderStatus) {
      case OrderStatus.PENDING:
        return 0;
      case OrderStatus.DISPATCHED:
        return 1;
      case OrderStatus.RECEIVED:
        return 2;
      case OrderStatus.STORED:
        return 2;
      case OrderStatus.PROCESSED:
        return 4;
      default:
        return 0;
    }
  };

  const qrValue = user?.getSignInUserSession ? user?.getSignInUserSession()?.getIdToken().payload['sub'] : null; //user?.idToken?.payload['cognito:sub'];

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const displaySampleKit = () => {
    return (
      <>
        {(participant.orderStatus === OrderStatus.PENDING ||
          participant.orderStatus === OrderStatus.HOLD ||
          participant.orderStatus === OrderStatus.CONFIRMED ||
          participant.orderStatus === OrderStatus.CREATED) && (
          <Message title={t('samples.messages.pending.title', { ns: 'portal' }) as string} severity="info">
            {t('samples.messages.pending.text', { ns: 'portal' })}
          </Message>
        )}
        {participant.orderStatus === OrderStatus.DISPATCHED && (
          <Message title={t('samples.messages.dispatched.title', { ns: 'portal' }) as string} severity="info">
            {t('samples.messages.dispatched.text', { ns: 'portal' })}
          </Message>
        )}
        {(participant.orderStatus === OrderStatus.RECEIVED || participant.orderStatus === OrderStatus.STORED) && (
          <Message title={t('samples.messages.received.title', { ns: 'portal' }) as string} severity="info">
            {t('samples.messages.received.text', { ns: 'portal' })}
          </Message>
        )}
        <Box mb={2} sx={{ overflowY: { xs: 'auto' } }}>
          <FormStepper
            activeStep={getStatusStep(participant?.orderStatus as string)}
            steps={['Preparing', 'Dispatched', 'Received', 'Processed']}
          />
        </Box>
        <Typography textAlign="left" variant="h6" component="h2" paragraph>
          {t('title1')}
        </Typography>
        <Typography textAlign="left" variant="body1" fontWeight="bold" paragraph>
          {t('text1')}
        </Typography>
        <Typography textAlign="left" variant="body1" component="span" paragraph>
          <ReactMarkdown>{t('text2')}</ReactMarkdown>
        </Typography>

        <Box py={3} px={isMobile ? 0 : 3} textAlign="center">
          <iframe
            width={isMobile ? '100%' : '560'}
            height={isMobile ? 'auto' : '315'}
            src="https://www.youtube-nocookie.com/embed/CE5roPspO3o"
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </Box>
        <Tabs>
          <div title="Tips">
            <Typography textAlign="left" variant="h6" component="h2" paragraph>
              {t('title2')}
            </Typography>
            <SectionBox color="info">
              <TipRow icon={MdOutlineNoFood} markdown={t('text3', { studyEmail: STUDY_EMAIL })} />
              <TipRow icon={MdOutlineToday} markdown={t('text4', { studyEmail: STUDY_EMAIL })} />
              <TipRow icon={MdOutlinePerson} markdown={t('text5', { studyEmail: STUDY_EMAIL })} />
              <TipRow icon={MdOutlinePeople} markdown={t('text6', { studyEmail: STUDY_EMAIL })} />
              <TipRow icon={RiFileDamageLine} markdown={t('text7', { studyEmail: STUDY_EMAIL })} />
            </SectionBox>
          </div>
          <div title="Instructions">
            <Typography textAlign="left" variant="h6" component="h2" paragraph>
              {t('title3')}
            </Typography>
            {Object.values(t('instructions', { returnObjects: true })).map((instruction, iIndex) => (
              <InstructionRow key={`instruction-${iIndex}`} markdown={instruction} number={iIndex + 1} />
            ))}
          </div>
        </Tabs>
      </>
    );
  };

  const displayCommunityEnrolmentEvents = () => {
    return (
      <Flags authorizedFlags={[FEATURES.COMMUNITY_ENROLMENT]}>
        <Box p={3}>
          <Typography variant="h5" align="center" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            {t('enrol.success.sampleTitle')}
          </Typography>

          {qrValue ? (
            <>
              <Typography variant="body1" align="center" paragraph>
                {t('enrol.success.sampleEventText')}
              </Typography>
              <Box
                role="img"
                aria-label="QR code for linking your saliva sample"
                sx={{
                  width: '100%',
                  maxWidth: 360,
                  mx: 'auto',
                  my: 4,
                  p: 2,
                  bgcolor: '#fff',
                  borderRadius: 2,
                  boxSizing: 'border-box',
                }}
              >
                <QRCode
                  value={qrValue}
                  size={320}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                  }}
                />
              </Box>
            </>
          ) : (
            <Typography role="status" color="text.secondary" align="center" sx={{ my: 4 }}>
              {t('enrol.success.samplePostText')}
            </Typography>
          )}

          <Typography align="center" variant="body1">
            {t('enrol.success.samplePostText')}
          </Typography>
        </Box>
      </Flags>
    );
  };

  return (
    <PortalLayout>
      <Typography textAlign="center" variant="h5" component="h1" paragraph>
        {t('samples.title', { ns: 'portal' })}
      </Typography>
      <SectionBox>
        {participant.orderStatus === OrderStatus.HOLD ? (
          <Tabs>
            <div title="Sample Kit">{displaySampleKit()}</div>
            <div title="Community Enrolment Events">{displayCommunityEnrolmentEvents()}</div>
          </Tabs>
        ) : (
          displaySampleKit()
        )}
      </SectionBox>
    </PortalLayout>
  );
}

export default SamplesScene;
